import { maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { throttle } from 'lodash';
import type { ReduxProxy } from 'v5/api/_common/reduxProxy';
import type {
  BoardRef,
  ShortlistBoardApi
} from '../../../api/shortlist-board/shortlistBoard.api';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import type { WithServices } from '../../../platform/ts/services';
import type { WithErrorService } from '../../front/ErrorService/ErrorService';
import type { WithMessageService } from '../../internal/MessageService/MessageService';
import type { WithMetastructureMappingService } from '../../internal/MetastructureMappingService/MetastructureMappingService';
import { type ShortlistBoardService } from './ShortlistBoardService';

const boardKey = (publicId: string) => ['shortlist-board', publicId];
const refKey = (board: BoardRef) => [
  ...boardKey(board.publicId),
  board.view,
  board.password
];
const clientSummaryKey = (publicId: string, password: string | null) =>
  refKey({ publicId, view: 'client', password }).concat('summary');

export function createShortlistBoardService(
  config: WithServices<
    [WithMessageService, WithErrorService, WithMetastructureMappingService]
  > & {
    api: ShortlistBoardApi;
    queryClient: QueryClient;
    reduxProxy: Pick<ReduxProxy, 'read'>;
  }
): ShortlistBoardService {
  const {
    api,
    queryClient,
    reduxProxy,
    services: { messageService, errorService, metastructureMappingService }
  } = config;
  const interceptor = errorService.withCategory('ShortlistBoardService');

  async function refresh(queryKey: unknown[]) {
    queryClient.removeQueries({ queryKey, type: 'inactive' });
    await queryClient.invalidateQueries({ queryKey, type: 'active' });
  }

  const clientSummaryQuery = (publicId: string, password: string | null) => ({
    queryKey: clientSummaryKey(publicId, password),
    queryFn: interceptor.wrap(() => api.getClientSummary(publicId, password))
  });

  /** The client view filters on the card fields, typed by its summary; Edit on the app's types. */
  async function fieldMappingsOf(board: BoardRef) {
    if (board.view === 'board') {
      return metastructureMappingService.getAllFieldNameTypeMappings();
    }
    const summary = await queryClient.fetchQuery(
      clientSummaryQuery(board.publicId, board.password)
    );
    return summary.restricted ? {} : summary.fieldMappings;
  }

  // The v1 shortlist page and sidebar still write blocks, votes and contacts.
  const refreshAll = throttle(() => refresh(['shortlist-board']), 3000, {
    leading: true,
    trailing: true
  });
  reduxProxy.read.addListener(event => {
    switch (event.type) {
      case 'UPDATE_SHORTLIST_SUCCESS':
      case 'DELETE_SHORTLIST_SUCCESS':
      case 'UPDATE_SHORTLIST_BLOCK_SUCCESS':
      case 'COMMENT_SHORTLIST_BLOCK_SUCCESS':
      case 'CREATE_SHORTLIST_BLOCK_SUCCESS':
      case 'UPDATE_CONTACT_SUCCESS':
      case 'MERGE_RESOURCE_SUCCESS':
        void refreshAll();
    }
  });

  messageService.reportSystemEffect.subscribeToRequest(async message => {
    try {
      for (const effect of message.request.scope) {
        switch (effect.entity) {
          // boards are read by their link, an effect names the shortlist's id or a block's
          case 'shortlist':
          case 'shortlistBlock':
          // cards carry the contact's name, picture and card fields
          case 'resource':
            await refresh(['shortlist-board']);
            break;
        }
      }
    } finally {
      message.sendResponse();
    }
  });

  return {
    useBoardSummary: (shortlist, scope) =>
      ensureIdleQuery(
        shortlist,
        useQuery(
          {
            enabled: maybe.isPresent(shortlist),
            queryKey: [
              ...boardKey(String(shortlist)),
              'board',
              null,
              'summary',
              scope
            ],
            queryFn: interceptor.wrap(() =>
              api.getBoardSummary(maybe.getOrThrow(shortlist), scope)
            )
          },
          queryClient
        )
      ),

    useClientSummary: (publicId, password) =>
      ensureIdleQuery(
        publicId,
        useQuery(
          {
            ...clientSummaryQuery(publicId ?? '', password),
            enabled: maybe.isPresent(publicId)
          },
          queryClient
        )
      ),

    useBlockPins: (board, blockId) =>
      ensureIdleQuery(
        board ? blockId : null,
        useQuery(
          {
            enabled: maybe.isPresent(board) && maybe.isPresent(blockId),
            queryKey: [
              ...(board ? refKey(board) : ['shortlist-board', 'none']),
              'block-pins',
              blockId
            ],
            queryFn: interceptor.wrap(() =>
              api.getBlockPins(
                maybe.getOrThrow(board),
                maybe.getOrThrow(blockId)
              )
            )
          },
          queryClient
        )
      ),

    useBlockSearch: (board, query, previewPins) =>
      ensureIdleQuery(
        board,
        useQuery(
          {
            enabled: maybe.isPresent(board),
            queryKey: [
              ...(board ? refKey(board) : ['shortlist-board', 'none']),
              'blocks',
              query,
              previewPins
            ],
            queryFn: interceptor.wrap(
              async ({ signal }: { signal: AbortSignal }) => {
                const present = maybe.getOrThrow(board);
                return api.getAllBlocks(
                  present,
                  await fieldMappingsOf(present),
                  query,
                  previewPins,
                  signal
                );
              }
            )
          },
          queryClient
        )
      )
  };
}
